import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import {
    timeSeriesXAxis,
    colors,
    lineGraphDotSize,
    lineGridStyle,
} from '../../../../shared/constants/constants';
import CustomTooltip from '../../CustomTooltip';
import {
    getXAxisLabel,
    getYAxisLabel,
    getChartMargin,
    getFormattedValue,
} from '../../../dashboardUtils';

function TimeSeriesLineGraph({
    chartProperties,
    dataFormatter,
    dataPoints,
    dataLabels,
    dateFormatter,
    legendFormatter,
    thresholds,
    xAxisLabel,
    yAxisLabelLeft,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        colorPalette = colors,
        lineChartProperties,
        lineProperties,
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend,
        suffix = '',
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const yAxisTickFormatter = (value) =>
        getFormattedValue(value, suffix, dataFormatter, timeFormat);

    const chartMargin = getChartMargin(
        dataPoints,
        timeSeriesXAxis,
        suffix,
        thresholds,
        timeFormat,
        yAxisTickFormatter,
        yAxisLabelLeft
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <LineChart {...lineChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray={lineGridStyle} />
                <XAxis
                    {...xAxisProperties}
                    dataKey={timeSeriesXAxis}
                    tickFormatter={dateFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    label={getYAxisLabel(yAxisLabelLeft)}
                    tickFormatter={yAxisTickFormatter}
                    width={chartMargin.left}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={dateFormatter}
                            axisKey={timeSeriesXAxis}
                            dataFormat={timeFormat}
                            dataFormatter={dataFormatter}
                            legendFormatter={legendFormatter}
                            suffix={suffix}
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {dataLabels.map((dataLabel, index) => (
                    <Line
                        {...lineProperties}
                        key={dataLabel}
                        dataKey={dataLabel}
                        stroke={colorPalette[index % colorPalette.length]}
                        activeDot={{
                            r: lineGraphDotSize,
                            onMouseOver: () => setActiveIndex(index),
                            onMouseOut: () => setActiveIndex(-1),
                            onFocus: () => setActiveIndex(index),
                            onBlur: () => setActiveIndex(-1),
                        }}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        y={threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

TimeSeriesLineGraph.propTypes = {
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataFormatter: PropTypes.func.isRequired,
    dataPoints: PropTypes.instanceOf(Array),
    dataLabels: PropTypes.instanceOf(Array),
    dateFormatter: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
};

TimeSeriesLineGraph.defaultProps = {
    dataPoints: [],
    dataLabels: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
};

export default React.memo(
    TimeSeriesLineGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
